import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { GetJob } from '../../api/jobs-endpoint';
import AcceptButton from '../../components/AcceptButton';
import SnackbarAlert from '../../components/Alerts';
import DeleteJobButton from '../../components/jobs/DeleteJobButton';
import JobRulesTabPanel from '../../components/jobs/JobRulesTabPanel';
import JobTabPanel from '../../components/jobs/JobTabPanel';
import JobVariablesTabPanel from '../../components/jobs/JobVariablesTabPanel';
import JobGroupsTabPanel from '../../components/jobs/JobGroupsTabPanel';
import useConfigStyles from '../../components/useConfigStyles';
import { getLinkToEditJob, getLinkToCopyJob } from '../../navigator/links';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Job } from '../../types/job';
import { defaultJob } from '../../types/defaultValues';
import useOperationAuthorizationChecks from '../../hooks/useOperationAuthorizationChecks';
import { UserOperations } from '../../types/user';

function JobDetailsPage(): JSX.Element {
  const { jobName } = useParams<{ jobName: string }>();
  const [job, setJob] = useState<Job>(defaultJob);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [notify, toggleNotification] = useState(false);
  const classes = useConfigStyles();
  const navigate = useNavigate();
  const canEditJob = useOperationAuthorizationChecks([UserOperations.JOB_EDIT]);
  const canCopyJob = useOperationAuthorizationChecks([UserOperations.JOB_CREATE]);

  const fetchJob = useCallback(async () => {
    if (jobName) {
      const response = await GetJob(jobName);
      if (response.success && response.data) {
        setJob(response.data);
      } else {
        setResponseMessage(response.message || '');
      }
      setFetched(response.success);
    }
  }, [jobName]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const handleTabChange = (event: ChangeEvent<unknown>, newValue: number) => {
    setTabValue(newValue);
  };

  const DeleteButton = () => (
    <DeleteJobButton
      job={job}
      className={classes.button}
      onDeleteFail={(message) => {
        setResponseMessage(message);
        toggleNotification(true);
      }}
    />
  );

  const handleCloseSnackbar = () => {
    toggleNotification(false);
  };

  const handleEditClick = () => {
    navigate(getLinkToEditJob(jobName!));
  };

  const handleCopyClick = () => {
    navigate(getLinkToCopyJob(jobName!));
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <>
      <Paper elevation={3} className={classes.paper}>
        <Tabs value={tabValue} indicatorColor='primary' textColor='primary' onChange={handleTabChange} centered className={classes.tabs} variant='fullWidth'>
          <Tab label='Job' />
          <Tab label='Job Variables' />
          <Tab label='Job Rules' />
          <Tab label='Job Groups' />
        </Tabs>
        <Card>
          <CardContent>
            <JobTabPanel job={job} index={0} selectedTab={tabValue} editable={false} />
            <JobVariablesTabPanel schoolYear={job.SchoolYear} variables={job.Variables} index={1} selectedTab={tabValue} editable={false} />
            <JobRulesTabPanel rules={job.Rules} index={2} selectedTab={tabValue} editable={false} />
            <JobGroupsTabPanel groups={job.Groups} index={3} selectedTab={tabValue} editable={false} />
          </CardContent>
          <CardActions>
            <Grid container alignItems='center' justifyContent='center' direction='row' spacing={'1vw'}>
              <Grid item>{canEditJob && <AcceptButton label='Edit Job' className={classes.button} onClick={handleEditClick} />}</Grid>
              <Grid item>{canCopyJob && <AcceptButton label='Copy Job' className={classes.button} onClick={handleCopyClick} />}</Grid>
              <Grid item>
                <DeleteButton />
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </Paper>
      <SnackbarAlert open={notify} message={responseMessage} handleClose={handleCloseSnackbar} />
    </>
  );
}

export default JobDetailsPage;
